import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload } from 'react-icons/fi';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { LoadingGrid } from '../../components/UI/Loading';
import { vendedoresService } from '../../services';
import { exportToCsv, exportToPdf } from '../../utils/exportReport';

const VendedorVentas = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [ventas, setVentas] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVentas();
  }, [id]);

  const loadVentas = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await vendedoresService.getVentasVendedor(id);

      if (result.success) {
        // Transformar los datos de la API
        const ventasData = result.data.map(venta => ({
          id: venta.id,
          fecha: new Date(venta.invoice_date || venta.created_at).toLocaleDateString('es-ES'),
          cliente: venta.client_name || venta.client?.company_name || 'N/A',
          total: parseFloat(venta.total_amount || venta.total || 0),
          estado: venta.status === 'PAID' ? 'Pagada' :
                  venta.status === 'PENDING' ? 'Pendiente' :
                  venta.status === 'CANCELLED' ? 'Cancelada' : 'Otro'
        }));
        setVentas(ventasData);
      } else {
        setError(result.message || 'Error al cargar las ventas');
        setVentas([]);
      }
    } catch (err) {
      console.error('Error cargando ventas:', err);
      setError('Error de conexión al cargar las ventas');
      setVentas([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(n || 0);

  const handleExport = (format) => {
    if (ventas.length === 0) {
      alert('No hay ventas para exportar.');
      return;
    }
    const columns = [
      { label: 'Fecha', value: (v) => v.fecha },
      { label: 'Cliente', value: (v) => v.cliente },
      { label: 'Total', value: (v) => formatCurrency(v.total) },
      { label: 'Estado', value: (v) => v.estado }
    ];
    const stamp = new Date().toISOString().split('T')[0];
    const fileBase = `ventas_vendedor_${id}_${stamp}`;
    if (format === 'csv') {
      exportToCsv(columns, ventas, fileBase);
    } else {
      exportToPdf('Reporte de Ventas', columns, ventas, `Vendedor #${id}`);
    }
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando reporte de ventas..." />;
  }

  return (
    <div className="vendedor-ventas-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ margin: '20px' }}>
          <strong>Error:</strong> {error}
          <button
            className="btn btn--ghost btn--sm"
            onClick={loadVentas}
            style={{ marginLeft: '10px' }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/vendedores')}
          type="button"
        >
          <FiArrowLeft /> Volver a Vendedores
        </button>

        <h1 className="form-title">
          REPORTE DE VENTAS
        </h1>

        <div className="form-actions">
          <button className="btn btn--outline" onClick={() => handleExport('csv')} type="button">
            <FiDownload /> CSV
          </button>
          <button className="btn btn--primary" onClick={() => handleExport('pdf')} type="button">
            <FiDownload /> PDF
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="page-content">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>FECHA</th>
                <th>CLIENTE</th>
                <th className="text-right">TOTAL</th>
                <th className="text-center">ESTADO</th>
              </tr>
            </thead>
            <tbody>
              {ventas.map((venta) => (
                <tr key={venta.id}>
                  <td>{venta.fecha}</td>
                  <td>{venta.cliente}</td>
                  <td className="text-right">
                    <strong>{formatCurrency(venta.total)}</strong>
                  </td>
                  <td className="text-center">
                    <span className={`badge ${venta.estado === 'Pagada' ? 'badge-activo' : 'badge-warning'}`}>
                      {venta.estado}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Menú inferior */}
      <BottomTabMenu
        baseRoute={`/system/vendedores/${id}`}
        currentTab="ventas"
        tabs={[
          { id: 'datos', label: 'Datos', route: '' },
          { id: 'ventas', label: 'Reporte de Ventas', route: '/ventas' },
          { id: 'comisiones', label: 'Reporte de Comisiones', route: '/comisiones' },
          { id: 'clientes', label: 'Lista de Clientes', route: '/clientes' },
          { id: 'grafica', label: 'Gráfica de Ventas', route: '/grafica' },
          { id: 'notas', label: 'Notas', route: '/notas' }
        ]}
      />
    </div>
  );
};

export default VendedorVentas;
